import { useRef, useState } from 'react';

import { Flex } from '@revenuemarket/cds';
import { Colors, typographyStyle } from '@revenuemarket/design';
import { overlays } from '@revenuemarket/overlays';
import { useCloseWithESC } from '@revenuemarket/react';
import { Typography } from 'antd';

import { GroupTreeItem } from '~apis/custom-label';
import { FlattenTreeResult, flattenTree } from '~components/ui/HorizontalTreeTable/Table';

import GroupLabelInfo from './GroupLabelInfo';
import GroupLabelRemoveModal from './GroupLabelRemoveModal';
import GroupNewInfo from './GroupNewInfo';
import useLabelManage from './GroupTreeSelect/hooks/useLabelManage';
import LabelCreateButton from './common/component/LabelCreateButton';
import LabelRemoveButton from './common/component/LabelRemoveButton';
import PanelHeader from './common/component/PanelHeader';
import { PanelWrapper } from './common/styles';
import useGroupLabelTreeStore from './store/groupLabelTreeStore';

type Props = {
  activeKey: string;
  setActiveItem: (item?: FlattenTreeResult<GroupTreeItem>) => void;
  onNewCategoryClick?: (key: string) => void;
  viewType?: 'new' | 'info';
};

const GroupLabelManagementPanel: React.FC<Props> = ({ activeKey, viewType, setActiveItem }) => {
  const { groupLabelTree } = useGroupLabelTreeStore();
  const [modalOpened, setModalOpened] = useState(false);
  const flattenedTree: FlattenTreeResult<GroupTreeItem>[] = flattenTree(groupLabelTree);
  const activeItem = flattenedTree.find((item) => item.key === activeKey);

  const panelRef = useRef<HTMLDivElement>(null);

  const { onNewCategoryClick, onPanelClose, viewStatus, setViewStatus, namePath, keyPath, depth } =
    useLabelManage<GroupTreeItem>({
      activeKey,
      activeItem,
      setActiveItem,
      viewType,
      flattenedTree,
    });

  useCloseWithESC({
    handleClose: onPanelClose,
    ignore: modalOpened,
  });

  if (!activeItem?.value) {
    return null;
  }

  return (
    <PanelWrapper direction="column" gap={12} ref={panelRef}>
      <PanelHeader<GroupTreeItem>
        activeItem={activeItem}
        flattenedTree={flattenedTree}
        setActiveItem={setActiveItem}
        namePath={namePath}
        keyPath={keyPath}
        viewStatus={viewStatus}
      />
      {viewStatus === 'info' ? (
        <GroupLabelInfo
          namePath={activeItem.namePath}
          depth={depth}
          activeChildren={activeItem.children}
          activeKey={activeItem.key}
          parentKey={activeItem.parentId}
          {...activeItem.value}
        />
      ) : (
        <GroupNewInfo
          flattenedTree={flattenedTree}
          depth={activeItem.depth}
          onCancel={() => {
            setActiveItem(undefined);
          }}
          onSuccess={(treeKey) => {
            onPanelClose(treeKey);
          }}
          parentId={activeItem.parentId}
          {...activeItem.value}
        />
      )}
      {viewStatus !== 'new' ? (
        <Flex direction="column" gap={12}>
          <LabelCreateButton onClick={onNewCategoryClick}>하위 항목 생성</LabelCreateButton>
          <LabelRemoveButton
            onRemove={async () => {
              if (activeItem.children.length === 1 && !activeItem.children[0]?.value?.name) {
                overlays.open(({ isOpen, onClose }) => {
                  return (
                    <GroupLabelRemoveModal
                      setModalOpened={setModalOpened}
                      open={isOpen}
                      onCancel={onClose}
                      item={activeItem}
                      defaultStep={2}
                    />
                  );
                });
                return;
              }
              overlays.open(({ isOpen, onClose }) => (
                <GroupLabelRemoveModal
                  setModalOpened={setModalOpened}
                  open={isOpen}
                  onCancel={onClose}
                  item={activeItem}
                />
              ));
            }}
          >
            <Typography.Text
              style={{
                ...typographyStyle['14_400'],
                color: Colors.gray700,
              }}
            >
              삭제하기
            </Typography.Text>
          </LabelRemoveButton>
        </Flex>
      ) : null}
    </PanelWrapper>
  );
};

export default GroupLabelManagementPanel;
